'use client'

import { useQuery } from '@tanstack/react-query'
import { getUnreadNotificationCountAction } from '../actions/notification.actions'
import { queryCachePolicy } from '@/shared/state/query-cache-policy'
import type { NotificationUnreadCount } from '../types/notification.types'

export const unreadNotificationCountQueryKey = [
  'notifications',
  'unread-count',
] as const

/**
 * Polls the authoritative unread totals every 30s. Realtime push is
 * deferred (`PLAN.md` § 4 / `discrepancies.md` § 6); polling is the
 * stand-in until then.
 *
 * Caches the whole `NotificationUnreadCount` (app-scoped total **and**
 * personalized subset) under one key, so a single `setQueryData` moves
 * both numbers together and the /alerts pills can never disagree
 * mid-flight. Every badge that shows an unread number reads it from
 * here — never by counting the loaded list, which only ever knows about
 * the pages that happen to be in the cache.
 *
 * Also opts into focus refetching (the global QueryClient default is
 * `false`) so returning to the tab refreshes the count immediately —
 * the web analogue of mobile's app-resume refresh. `staleTime` gates
 * it, so rapid focus/blur flips within 15s don't trigger network calls.
 */
export function useUnreadNotificationCount(options?: { enabled?: boolean }) {
  return useQuery<NotificationUnreadCount>({
    queryKey: unreadNotificationCountQueryKey,
    queryFn: async () => {
      const res = await getUnreadNotificationCountAction()
      if (!res.success) throw new Error(res.error.userMessage)
      return res.data
    },
    enabled: options?.enabled ?? true,
    refetchInterval: queryCachePolicy.liveSummary.refetchInterval,
    refetchOnWindowFocus: true,
    staleTime: queryCachePolicy.liveSummary.staleTime,
  })
}
